import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, Mail, MessageSquare, Smartphone, Save, 
  AlertCircle, Check, ChevronRight, Settings
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { PartnerLayout } from '@/components/PartnerLayout';
import api from '@/lib/api';
import { toast } from 'sonner';

const PartnerNotifications = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isPartner, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Notification settings state
  const [settings, setSettings] = useState({
    // Email
    email_new_booking: true,
    email_new_reservation: true,
    email_new_inquiry: true,
    email_voucher_redeemed: true,
    email_daily_summary: false,
    // SMS
    sms_enabled: false,
    sms_phone_number: '',
    sms_new_booking: false,
    sms_new_reservation: false,
    sms_voucher_redeemed: false,
    // In-App
    in_app_enabled: true,
    in_app_new_booking: true,
    in_app_new_reservation: true,
    in_app_voucher_redeemed: true
  });

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    if (!isPartner) {
      navigate('/dashboard');
      return;
    }
    
    fetchData();
  }, [isAuthenticated, isPartner, authLoading]);

  const fetchData = async () => {
    try {
      // Fetch notification settings
      const settingsRes = await api.get('/partner/notifications/settings');
      setSettings(prev => ({ ...prev, ...settingsRes.data }));
      
      // Fetch recent notifications
      const notifRes = await api.get('/partner/notifications?limit=20');
      setNotifications(notifRes.data.notifications || []);
      setUnreadCount(notifRes.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notification data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/partner/notifications/settings', settings);
      toast.success('Notification settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.put('/partner/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark notifications as read');
    }
  };

  const handleToggle = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const formatNotificationTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'event': return '🎫';
      case 'experience': return '🎁';
      case 'reservation': return '📅';
      case 'voucher_redeemed': return '✅';
      default: return '🔔';
    }
  };

  if (!isAuthenticated || !isPartner) return null;

  return (
    <PartnerLayout
      title="Notifications"
      subtitle="Manage how you receive updates about bookings and activities"
    >
      {loading ? (
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
        </div>
      ) : (
        <div className="space-y-8">
          {/* Recent Notifications */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-indigo-600" />
                  Recent Notifications
                  {unreadCount > 0 && (
                    <Badge className="bg-red-500 text-white">{unreadCount} new</Badge>
                  )}
                </CardTitle>
                <CardDescription>Your latest booking and activity updates</CardDescription>
              </div>
              {unreadCount > 0 && (
                <Button variant="outline" size="sm" onClick={handleMarkAllRead}>
                  <Check className="w-4 h-4 mr-2" />
                  Mark All Read
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {notifications.length === 0 ? (
                <div className="text-center py-8">
                  <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No notifications yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notifications.map((notif) => (
                    <div
                      key={notif.id}
                      className={`p-4 rounded-lg border transition-colors ${
                        notif.read 
                          ? 'bg-background border-border' 
                          : 'bg-indigo-50 dark:bg-indigo-950/20 border-indigo-200 dark:border-indigo-800'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">{getNotificationIcon(notif.type)}</span>
                        <div className="flex-grow">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-foreground">{notif.title}</h4>
                            <span className="text-xs text-muted-foreground">
                              {formatNotificationTime(notif.created_at)}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{notif.message}</p>
                        </div>
                        {!notif.read && (
                          <div className="w-2 h-2 rounded-full bg-indigo-600 flex-shrink-0 mt-2" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notification Settings */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Email Notifications */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-indigo-600" />
                  Email Notifications
                </CardTitle>
                <CardDescription>Receive updates via email</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">New Bookings</p>
                    <p className="text-xs text-muted-foreground">Ticket sales & experience purchases</p>
                  </div>
                  <Switch
                    checked={settings.email_new_booking}
                    onCheckedChange={(v) => handleToggle('email_new_booking', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">New Reservations</p>
                    <p className="text-xs text-muted-foreground">Table & experience reservations</p>
                  </div>
                  <Switch
                    checked={settings.email_new_reservation}
                    onCheckedChange={(v) => handleToggle('email_new_reservation', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Voucher Redeemed</p>
                    <p className="text-xs text-muted-foreground">When a voucher is used</p>
                  </div>
                  <Switch
                    checked={settings.email_voucher_redeemed}
                    onCheckedChange={(v) => handleToggle('email_voucher_redeemed', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Inquiries</p>
                    <p className="text-xs text-muted-foreground">Private hire requests</p>
                  </div>
                  <Switch
                    checked={settings.email_new_inquiry}
                    onCheckedChange={(v) => handleToggle('email_new_inquiry', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Daily Summary</p>
                    <p className="text-xs text-muted-foreground">Daily activity digest</p>
                  </div>
                  <Switch
                    checked={settings.email_daily_summary}
                    onCheckedChange={(v) => handleToggle('email_daily_summary', v)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* SMS Notifications */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-green-600" />
                  SMS Notifications
                </CardTitle>
                <CardDescription>Receive updates via text message</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Enable SMS</p>
                    <p className="text-xs text-muted-foreground">Turn on text notifications</p>
                  </div>
                  <Switch
                    checked={settings.sms_enabled}
                    onCheckedChange={(v) => handleToggle('sms_enabled', v)}
                  />
                </div>
                
                {settings.sms_enabled && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        placeholder="(555) 123-4567"
                        value={settings.sms_phone_number}
                        onChange={(e) => handleToggle('sms_phone_number', e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">US phone numbers only</p>
                    </div>
                    
                    <div className="border-t pt-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-sm">New Bookings</p>
                        <Switch
                          checked={settings.sms_new_booking}
                          onCheckedChange={(v) => handleToggle('sms_new_booking', v)}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-sm">New Reservations</p>
                        <Switch
                          checked={settings.sms_new_reservation}
                          onCheckedChange={(v) => handleToggle('sms_new_reservation', v)}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-sm">Voucher Redeemed</p>
                        <Switch
                          checked={settings.sms_voucher_redeemed}
                          onCheckedChange={(v) => handleToggle('sms_voucher_redeemed', v)}
                        />
                      </div>
                    </div>
                  </>
                )}
                
                {!settings.sms_enabled && (
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
                    <Smartphone className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Enable SMS to receive instant text notifications
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* In-App Notifications */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-purple-600" />
                  In-App Notifications
                </CardTitle>
                <CardDescription>Notifications in your dashboard</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Enable In-App</p>
                    <p className="text-xs text-muted-foreground">Show bell icon notifications</p>
                  </div>
                  <Switch
                    checked={settings.in_app_enabled}
                    onCheckedChange={(v) => handleToggle('in_app_enabled', v)}
                  />
                </div>
                
                {settings.in_app_enabled && (
                  <div className="border-t pt-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-sm">New Bookings</p>
                      <Switch
                        checked={settings.in_app_new_booking}
                        onCheckedChange={(v) => handleToggle('in_app_new_booking', v)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-sm">New Reservations</p>
                      <Switch
                        checked={settings.in_app_new_reservation}
                        onCheckedChange={(v) => handleToggle('in_app_new_reservation', v)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-sm">Voucher Redeemed</p>
                      <Switch
                        checked={settings.in_app_voucher_redeemed}
                        onCheckedChange={(v) => handleToggle('in_app_voucher_redeemed', v)}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button 
              onClick={handleSaveSettings}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-notification-settings"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </PartnerLayout>
  );
};

export default PartnerNotifications;
